import { inject, Injectable } from '@angular/core';
import { Observable, map, shareReplay } from 'rxjs';
import { DATA_API_TOKEN } from './data-api.interface';
import { ContenutiDinamiciMap } from '../models/contenuto-dinamico.models';

export interface ContenutoStatico<T = Record<string, any>> {
  codContenuto: string;
  descrizione?: string;
  contenuto: T;
}

/**
 * Punto di accesso unico ai contenuti dinamici gestiti da BO (bo/contenuti-statici)
 * e consumati qui in FO (fo/contenuti-statici). La forma di ciascuna chiave è
 * dichiarata una sola volta nel registro centrale `ContenutiDinamiciMap`
 * (vedi core/models/contenuto-dinamico.models.ts): `getByKey` è generico su
 * quella mappa, così ogni nuovo consumer ottiene automaticamente il tipo
 * corretto — senza ridefinire un'interfaccia locale per ogni chiave — e lo
 * sblocco del payload (`contenuto.contenuto`) avviene qui una volta sola.
 *
 * `getByKey` non fa più una `GET` per chiave: si appoggia su `getAll()`, che
 * recupera tutti i contenuti in un'unica chiamata di rete. Grazie a
 * `shareReplay` + al controllo `if (!this.allCache)`, anche se home, footer e
 * progetto chiedono chiavi diverse nello stesso istante all'avvio, parte una
 * sola richiesta HTTP: la seconda richiesta (e tutte le successive) trovano
 * `allCache` già impostato e riusano lo stesso risultato.
 */
@Injectable({
  providedIn: 'root',
})
export class ContenutoStaticoService {
  private apiService = inject(DATA_API_TOKEN);

  private readonly ENDPOINT = '/fo/contenuti-statici';

  // Contenuto statico praticamente immutabile durante la sessione: la prima
  // chiamata (qualunque chiave la richieda) va in rete, tutte le successive
  // (altre chiavi, altri componenti, altre visite alla stessa pagina) riusano
  // lo stesso risultato senza rifare la richiesta.
  private allCache: Observable<ContenutoStatico[]> | null = null;

  getAll(): Observable<ContenutoStatico[]> {
    if (!this.allCache) {
      this.allCache = this.apiService
        .get<ContenutoStatico[]>(this.ENDPOINT)
        .pipe(shareReplay({ bufferSize: 1, refCount: false }));
    }
    return this.allCache;
  }

  /**
   * Recupera il contenuto dinamico associato alla chiave, tipizzato secondo
   * il registro centrale `ContenutiDinamiciMap`. Il payload può essere
   * parziale (BO potrebbe non aver ancora valorizzato tutti i campi): i
   * chiamanti devono unire il risultato con un fallback locale.
   */
  getByKey<K extends keyof ContenutiDinamiciMap>(
    key: K,
  ): Observable<Partial<ContenutiDinamiciMap[K]>> {
    return this.getAll().pipe(
      map((tutti) => tutti.find((c) => c.codContenuto === key)),
      map((trovato) => (trovato?.contenuto ?? {}) as Partial<ContenutiDinamiciMap[K]>),
    );
  }
}
